/**
 * Inbox as a first-class cross-project surface (docs/ux/project-tab-spec.md §1.9): one row per
 * thing that needs you (decisions, approvals, landings, releases, proposals…) with its project,
 * lane, asking agent, needs-you reason, age, suggested answer and quick-picks; grouped by leverage
 * (decision sessions batched) or by project; filters; bulk actions; keyboard triage; zero inbox.
 * The detail (decision view or the item's card) opens in the inspector (`?i=<key>`).
 */
import { useNavigate } from '@tanstack/react-router'
import { StatusIcon } from '@viko/transcript-ui'
import {
  AlarmClock,
  CircleCheck,
  Diamond,
  GitMerge,
  Info,
  Keyboard,
  ListChecks,
  Package,
  Rocket,
  ScrollText,
  ShieldQuestion,
  SlidersHorizontal,
  Sparkles,
  TriangleAlert,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useBackend } from '../../lib/backend.js'
import {
  type BreatheStrength,
  type BreatheTone,
  breatheClass,
  useBreathePause,
} from '../../lib/breathe.js'
import { SNOOZE_OPTIONS, snoozeUntil } from '../../lib/inbox.js'
import { actOnCard } from '../../lib/inbox-actions.js'
import { isLockConsequence } from '../../lib/locked-view.js'
import { isBlockedRow, needsYouCount } from '../../lib/needs-you.js'
import {
  filterInboxRows,
  flatKeys,
  groupInboxRows,
  type InboxEntry,
  type InboxFilter,
  type InboxGroupView,
  type InboxKind,
  type InboxRow,
  KIND_LABEL,
  type TriageAction,
} from '../../lib/now-inbox.js'
import { isLive, useProjects, useRuns } from '../../lib/queries.js'
import { inboxRowEntity } from '../../lib/row-actions.js'
import { rowMeta, rowTitle, shortAgo } from '../../lib/row-meta.js'
import { type StableShape, useInteracting, useStableGroups } from '../../lib/stable-order.js'
import type { InboxItem } from '../../lib/types.js'
import { useAppUi } from '../app-ui.js'
import { useDecisionActions } from '../decision/DecisionPanel.js'
import { OptionKey, SuggestedPill } from '../decision/OptionList.js'
import { LandAllBar } from '../LandAll.js'
import { useLocked } from '../lock/ContentLock.js'
import { RowActionBar, TRIAGE_KEYS, useRowActions } from '../RowActions.js'
import { Ago, errorText, PageHead, SkeletonRows } from '../ui.js'
import { useAskActions } from './useAskActions.js'
import { useInboxRows } from './useInboxRows.js'
import { useTabOrder } from './useTabOrder.js'
import { useTriageKeys } from './useTriageKeys.js'
import './triage.css'

const INBOX_SHAPE: StableShape<InboxGroupView, InboxEntry> = {
  groupKey: (g) => g.key,
  entries: (g) => g.entries,
  entryKey: (e) => (e.type === 'row' ? e.row.key : `batch:${e.id}`),
  withEntries: (g, entries) => ({ ...g, entries }),
}

export type OpenIntent = 'reply' | 'delegate'

/** Stale-resolved items already marked done in this page session. */
const autoResolved = new Set<string>()

export interface InboxTriageProps {
  /** The inspector key open in the detail pane. */
  current?: string
  onOpen: (key: string, intent?: OpenIntent) => void
  onClose: () => void
  /** Rendered inside a drawer (no page heading). */
  embedded?: boolean
  /** Extra sections below the list (snoozed hub items, builds digest). */
  footer?: ReactNode
  /** Pre-filter (a project tab's badge). */
  projectId?: string
  /** Keyboard triage owns its keys (drawer over the Lanes tab). */
  exclusiveKeys?: boolean
  /** Receives the row order (the inspector opens the next row after an answer). */
  orderRef?: { current: readonly string[] }
  /** ia-shell: j/k triage keys (off when another list on the page owns them). */
  keysEnabled?: boolean
  /**
   * core-loop: the top row is the cursor as soon as the list loads (Home on desktop), so `1`–`9`
   * answer it and `⏎` opens its context with no `j` first.
   */
  preselect?: boolean
}

const KIND_ICON: Record<InboxKind, ReactNode> = {
  decision: <Diamond aria-hidden="true" />,
  blocked: <TriangleAlert aria-hidden="true" />,
  approval: <ShieldQuestion aria-hidden="true" />,
  landing: <GitMerge aria-hidden="true" />,
  release: <Rocket aria-hidden="true" />,
  proposal: <ListChecks aria-hidden="true" />,
  rule: <ScrollText aria-hidden="true" />,
  build: <Package aria-hidden="true" />,
  alert: <TriangleAlert aria-hidden="true" />,
  card: <Sparkles aria-hidden="true" />,
}

/** One baseline row per group head: title, count, then what the group means (text-2). */
const GROUP_DESC: Record<string, string> = {
  blocked: 'Can’t continue until fixed',
  blocking: 'Waiting on your decision',
  unblocks: 'Other work waits on these',
  later: 'When convenient',
}

function load<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  try {
    const v = localStorage.getItem(key) as T | null
    return v && allowed.includes(v) ? v : fallback
  } catch {
    return fallback
  }
}
function save(key: string, v: string) {
  try {
    localStorage.setItem(key, v)
  } catch {
    // private window: per-session only
  }
}

export function InboxTriage(p: InboxTriageProps) {
  const nav = useNavigate()
  const built = useInboxRows()
  const locked = useLocked()
  const { inbox, decisions } = built
  const runs = useRuns()
  const projects = useProjects()
  const tabs = useTabOrder()
  const act = useDecisionActions()
  const ask = useAskActions()
  const toast = act.toast
  const [mode, setMode] = useState<'leverage' | 'project'>(() =>
    load('viko.inbox.group', 'leverage', ['leverage', 'project'] as const),
  )
  const [filter, setFilter] = useState<InboxFilter>(p.projectId ? { projectId: p.projectId } : {})
  const ui = useAppUi()
  const [showAssumptions, setShowAssumptions] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [done, setDone] = useState<ReadonlySet<string>>(new Set())
  const root = useRef<HTMLDivElement>(null)

  const rows = useMemo(
    () =>
      filterInboxRows(built.rows, filter).filter(
        // Locked: machine blockers that only wait on this browser's lock sit under the lock panel.
        (r) => !done.has(r.key) && !(locked && isLockConsequence(r)),
      ),
    [built.rows, filter, done, locked],
  )
  const liveGroups = useMemo(() => groupInboxRows(rows, mode, tabs.order), [rows, mode, tabs.order])
  // Rows keep their place while the pointer or focus is in the list; the new order lands on idle.
  const groups = useStableGroups(liveGroups, useInteracting(root), INBOX_SHAPE)
  const keys = useMemo(() => flatKeys(groups), [groups])
  if (p.orderRef) p.orderRef.current = keys
  const byKey = useMemo(() => new Map(rows.map((r) => [r.key, r])), [rows])

  const gone = (key: string) => {
    setDone((d) => new Set([...d, key]))
    // Fast triage: the next row opens where the answered one was.
    if (p.current === key) {
      const i = keys.indexOf(key)
      const next = keys[i + 1] ?? keys[i - 1]
      if (next && !next.startsWith('batch:')) p.onOpen(next)
      else p.onClose()
    }
  }
  // Items the hub or the store dropped come back only if they reappear with new data.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on new data
  useEffect(() => setDone(new Set()), [inbox.data, decisions.data])
  // inbox-detail: items whose condition no longer holds are marked done once (every device).
  const b = useBackend()
  useEffect(() => {
    for (const i of built.resolved) {
      const id = i.itemId ?? i.id
      if (autoResolved.has(id)) continue
      autoResolved.add(id)
      void actOnCard(b.api, i, 'done').catch(() => autoResolved.delete(id))
    }
  }, [built.resolved, b])

  // Audit / info notices (unlocked via…): a dismissible line, never a row or a count.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set())
  const notices = built.notices.filter((n) => !dismissed.has(n.itemId ?? n.id))
  const dismissNotice = async (n: InboxItem) => {
    setDismissed((d) => new Set([...d, n.itemId ?? n.id]))
    try {
      await actOnCard(b.api, n, 'done')
    } catch {
      // stays dismissed here; the hub copy lingers until the next successful dismiss
    }
  }

  const quick = async (row: InboxRow, optionId: string) => {
    if (!row.view) return
    try {
      if (await act.answer(row.view, { mode: 'pick', optionId })) {
        toast(`Answered: ${row.quick?.find((q) => q.id === optionId)?.label ?? 'picked'}`)
        gone(row.key)
      }
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  const snoozeRows = async (list: InboxRow[], until: number) => {
    let n = 0
    for (const r of list) {
      try {
        if (r.view) await act.snooze(r.view, until)
        else if (r.item) await ask.snooze(r.item, until)
        n++
        gone(r.key)
      } catch (e) {
        toast(errorText(e), 'error')
      }
    }
    if (n)
      toast(
        `Snoozed ${n === 1 ? '' : `${n} `}until ${new Date(until).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
      )
    triage.setSelected([])
  }
  const acceptSuggested = async (list: InboxRow[]) => {
    const ok = list.filter((r) => r.suggested && r.view && !r.risky)
    let n = 0
    for (const r of ok)
      try {
        if (await act.answer(r.view!, { mode: 'pick', optionId: r.suggested!.id })) {
          n++
          gone(r.key)
        }
      } catch (e) {
        toast(errorText(e), 'error')
      }
    const skipped = list.length - ok.length
    toast(
      `Sent ${n} suggested answer${n === 1 ? '' : 's'}${skipped ? `; ${skipped} need you one by one` : ''}`,
    )
    triage.setSelected([])
  }

  const onAction = (a: TriageAction) => {
    const key = 'key' in a ? a.key : undefined
    const row = key ? byKey.get(key) : undefined
    switch (a.type) {
      case 'open':
        return p.onOpen(a.key)
      case 'close':
        return p.onClose()
      case 'pick': {
        const q = row?.quick?.find((x) => x.key === a.n)
        if (row && q) void quick(row, q.id)
        return
      }
      case 'suggested':
        if (row?.suggested && !row.risky) void quick(row, row.suggested.id)
        else if (row) p.onOpen(row.key)
        return
      case 'reply':
        return key ? p.onOpen(key, 'reply') : undefined
      case 'delegate':
        return key ? p.onOpen(key, 'delegate') : undefined
      case 'snooze':
        return void snoozeRows(
          a.keys.map((k) => byKey.get(k)).filter((r): r is InboxRow => !!r),
          snoozeUntil(SNOOZE_OPTIONS[1].ms),
        )
      case 'project':
        if (row?.projectId && projects.data?.some((x) => x.id === row.projectId))
          void nav({ to: '/p/$projectId', params: { projectId: row.projectId } })
        return
      case 'help':
        return ui.openShortcuts?.() // keyboard-shortcuts: ? opens the global cheat sheet
    }
  }
  const triage = useTriageKeys({
    keys,
    onAction,
    root,
    // j/k with an inspector open swap its content (the batch row has no inspector body to swap).
    ...(p.current
      ? { current: p.current, onMove: (k: string) => !k.startsWith('batch:') && p.onOpen(k) }
      : {}),
    exclusive: !!p.exclusiveKeys,
    enabled: p.keysEnabled !== false,
  })
  // core-loop: preselect the top row (never over an open inspector or a moved cursor). The cursor
  // starts on the top answerable item (1 key answers it); Blocked rows stay listed first, but a
  // fix is not a key press. Until you move it, the cursor follows the list as decisions load.
  const preselected = useRef<{ index?: number; user?: boolean }>({})
  useEffect(() => {
    const auto = preselected.current
    if (!p.preselect || auto.user || p.current) return
    // The cursor index moved away from where we put it: that was you (j/k, a click).
    if (triage.focus >= 0 && triage.focus !== auto.index) {
      auto.user = true
      return
    }
    const answerable = keys.findIndex((k) => !k.startsWith('batch:') && !!byKey.get(k)?.quick)
    const first = answerable >= 0 ? answerable : keys.findIndex((k) => !k.startsWith('batch:'))
    if (first < 0 || first === triage.focus) return
    auto.index = first
    triage.setFocus(first)
  }, [p.preselect, p.current, keys, triage, byKey])
  const selectedRows = [...triage.selected]
    .map((k) => byKey.get(k))
    .filter((r): r is InboxRow => !!r)

  const running = (runs.data ?? []).filter(isLive).length
  const lastDecision = Math.max(0, ...(decisions.data ?? []).map((d) => d.answer?.at ?? 0))
  const loading = built.loading
  const filtered = !!(filter.projectId || filter.kind || filter.blockingOnly)
  // The glow is full on the item your keys are on (else the top waiting one), faint on the rest.
  const waiting = keys.filter((k) => byKey.get(k)?.blocking)
  const leadKey =
    p.current && byKey.get(p.current)?.blocking
      ? p.current
      : keys[triage.focus] && byKey.get(keys[triage.focus] as string)?.blocking
        ? keys[triage.focus]
        : waiting[0]
  const rowProps = (r: InboxRow) => ({
    breathe: r.blocking ? ((r.key === leadKey ? 'full' : 'faint') as BreatheStrength) : undefined,
    row: r,
    focused: keys[triage.focus] === r.key,
    current: p.current === r.key,
    selected: triage.selected.has(r.key),
    tab: tabs.label(r.projectId),
    onOpen: () => {
      triage.setFocus(keys.indexOf(r.key))
      p.onOpen(r.key)
    },
    onToggle: () => triage.toggle(r.key),
    onQuick: (id: string) => void quick(r, id),
    onApprove:
      r.item && r.kind === 'approval'
        ? (d: 'allow' | 'deny') => void ask.decide(r.item!, d).then((ok) => ok && gone(r.key))
        : undefined,
    onProject: () =>
      r.projectId && projects.data?.some((x) => x.id === r.projectId)
        ? void nav({ to: '/p/$projectId', params: { projectId: r.projectId } })
        : undefined,
  })

  const kinds = [...new Set(built.rows.map((r) => r.kind))]
  return (
    <div className={`tr${triage.selected.size ? ' is-selecting' : ''}`} ref={root}>
      {p.embedded ? null : (
        <PageHead title="Inbox">
          <button
            type="button"
            className="vk-btn vk-btn--ghost vk-btn--sm"
            onClick={() => ui.openShortcuts?.()}
            aria-keyshortcuts="?"
          >
            <Keyboard size={14} aria-hidden="true" />
            <span>Shortcuts</span>
          </button>
        </PageHead>
      )}
      {filtersOpen || filtered ? (
        <div className="tr-controls" role="toolbar" aria-label="Group and filter">
          <div className="tr-seg" role="group" aria-label="Group by">
            {(['leverage', 'project'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => {
                  setMode(m)
                  save('viko.inbox.group', m)
                }}
              >
                {m === 'leverage' ? 'Most impact first' : 'By project'}
              </button>
            ))}
          </div>
          <label className="sr-only" htmlFor="tr-f-project">
            Project
          </label>
          <select
            id="tr-f-project"
            className="tr-select"
            value={filter.projectId ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, projectId: e.target.value || undefined }))}
          >
            <option value="">All projects</option>
            {(projects.data ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
          <label className="sr-only" htmlFor="tr-f-kind">
            Kind
          </label>
          <select
            id="tr-f-kind"
            className="tr-select"
            value={filter.kind ?? ''}
            onChange={(e) =>
              setFilter((f) => ({
                ...f,
                kind: (e.target.value || undefined) as InboxKind | undefined,
              }))
            }
          >
            <option value="">All kinds</option>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
          <label className="tr-check">
            <input
              type="checkbox"
              checked={!!filter.blockingOnly}
              onChange={(e) =>
                setFilter((f) => ({ ...f, blockingOnly: e.target.checked || undefined }))
              }
            />
            Blocking only
          </label>
        </div>
      ) : null}
      {p.embedded ? null : <LandAllBar items={inbox.data ?? []} />}
      {loading ? <SkeletonRows n={4} /> : null}

      {!loading && !rows.length ? (
        filtered ? (
          <div className="tr-zero">
            <p className="tr-zero__h">Nothing matches these filters</p>
            <button
              type="button"
              className="vk-btn vk-btn--secondary vk-btn--sm"
              onClick={() => setFilter({})}
            >
              Show everything
            </button>
          </div>
        ) : (
          <section className="tr-zero" aria-labelledby="tr-zero-h">
            <p className="tr-zero__h" id="tr-zero-h">
              <CircleCheck size={20} aria-hidden="true" /> Nothing needs you
            </p>
            <p>
              {running} {running === 1 ? 'agent' : 'agents'} running
              {lastDecision ? (
                <>
                  . Last decision <Ago ts={lastDecision} />
                </>
              ) : null}
              .
            </p>
            {built.assumptions.length ? (
              <>
                <button
                  type="button"
                  className="dv-link"
                  aria-expanded={showAssumptions}
                  onClick={() => setShowAssumptions(!showAssumptions)}
                >
                  Assumptions made today ({built.assumptions.length})
                </button>
                {showAssumptions ? (
                  <ul className="tr-assumptions">
                    {built.assumptions.map((d) => (
                      <li key={d.id}>
                        <strong>{d.title ?? d.question}</strong>
                        <span>{d.resolution?.assumption}</span>
                        <button
                          type="button"
                          className="dv-link"
                          onClick={() => p.onOpen(`decision:${d.id}`)}
                        >
                          Review or change
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : null}
          </section>
        )
      ) : null}

      {!loading && rows.length && !filtered && !needsYouCount(rows) ? (
        <p className="tr-zero__h" data-testid="needs-you-zero">
          <CircleCheck size={20} aria-hidden="true" /> Nothing needs you
        </p>
      ) : null}
      {notices.map((n) => (
        <div key={n.itemId ?? n.id} className="tr-notice" role="status" data-testid="info-notice">
          <Info size={14} aria-hidden="true" />
          <span className="tr-grow">{n.title}</span>
          <Ago ts={n.createdAt} />
          <button type="button" className="dv-link" onClick={() => void dismissNotice(n)}>
            Dismiss
          </button>
        </div>
      ))}

      {groups.map((g) => (
        <section key={g.key} className="tr-group" aria-labelledby={`tr-g-${g.key}`}>
          <div className="tr-group__hrow">
            <h2
              id={`tr-g-${g.key}`}
              className={`tr-group__h${g.hot ? ' is-hot' : ''}`}
              data-group={g.key}
            >
              <span className="tr-group__t">{g.title}</span>
              <span className="tr-group__n">
                {g.entries.reduce((n, e) => n + (e.type === 'row' ? 1 : e.rows.length), 0)}
              </span>
              {GROUP_DESC[g.key] ? <span className="tr-group__d">{GROUP_DESC[g.key]}</span> : null}
            </h2>
            {g === groups[0] ? (
              <span className="tr-group__aside">
                <span className="tr-keyhint" aria-hidden="true">
                  <kbd>1–9</kbd>answer
                </span>
                <button
                  type="button"
                  className="tr-filter-btn"
                  aria-expanded={filtersOpen || filtered}
                  onClick={() => setFiltersOpen((x) => !x)}
                >
                  <SlidersHorizontal size={14} aria-hidden="true" />
                  Filter
                </button>
              </span>
            ) : null}
          </div>
          <ul className="tr-list">
            {g.entries.map((e) =>
              e.type === 'row' ? (
                <TriageRow key={e.row.key} {...rowProps(e.row)} />
              ) : (
                <li key={`batch:${e.id}`} className="tr-session" data-row-key={`batch:${e.id}`}>
                  <div
                    className={`tr-session__head${keys[triage.focus] === `batch:${e.id}` ? ' is-focused' : ''}`}
                  >
                    <button
                      type="button"
                      data-row-main
                      onClick={() => p.onOpen(`batch:${e.id}`)}
                      aria-label={`${e.title}. Decide all`}
                    >
                      <ListChecks size={15} aria-hidden="true" />
                      <span>{e.title}</span>
                    </button>
                    <button
                      type="button"
                      className="vk-btn vk-btn--secondary vk-btn--sm"
                      tabIndex={-1}
                      onClick={() => p.onOpen(`batch:${e.id}`)}
                    >
                      Decide all
                    </button>
                  </div>
                  <ul className="tr-list">
                    {e.rows.map((r) => (
                      <TriageRow key={r.key} {...rowProps(r)} />
                    ))}
                  </ul>
                </li>
              ),
            )}
          </ul>
        </section>
      ))}

      {selectedRows.length ? (
        <div className="tr-bulk" role="region" aria-label="Selected items">
          <span className="tr-bulk__n">{selectedRows.length} selected</span>
          <button
            type="button"
            className="vk-btn vk-btn--primary vk-btn--sm"
            disabled={!selectedRows.some((r) => r.suggested && !r.risky)}
            onClick={() => void acceptSuggested(selectedRows)}
          >
            Accept suggested
          </button>
          {SNOOZE_OPTIONS.map((o) => (
            <button
              key={o.label}
              type="button"
              className="vk-btn vk-btn--secondary vk-btn--sm"
              onClick={() => void snoozeRows(selectedRows, snoozeUntil(o.ms))}
            >
              <AlarmClock size={13} aria-hidden="true" />
              <span>Snooze {o.label.toLowerCase()}</span>
            </button>
          ))}
          <button
            type="button"
            className="vk-btn vk-btn--ghost vk-btn--sm"
            onClick={() => triage.setSelected([])}
          >
            Clear
          </button>
          {selectedRows.some((r) => r.risky) ? (
            <span className="tr-hint">
              Landings, releases and risky decisions need your passkey one by one.
            </span>
          ) : null}
        </div>
      ) : null}
      <SnoozedDecisions rows={built.snoozed} />
      {p.footer}
    </div>
  )
}

interface RowProps {
  /** design-language §9: the needs-input glow (full on the top item, faint on the rest). */
  breathe?: BreatheStrength | undefined
  row: InboxRow
  focused: boolean
  current: boolean
  selected: boolean
  tab: string | undefined
  onOpen: () => void
  onToggle: () => void
  onQuick: (optionId: string) => void
  onApprove?: ((d: 'allow' | 'deny') => void) | undefined
  onProject: () => void
  /** Quick-picks reachable with Tab (outside the keyboard-triage lists). */
  quickTabbable?: boolean
  /** Bulk selection offered (the Inbox and Now lists; not the Lanes asks panel). */
  selectable?: boolean
}

export function TriageRow(r: RowProps) {
  const { row } = r
  const press = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const li = useRef<HTMLLIElement>(null)
  const acts = useRowActions(inboxRowEntity(row), { skipKeys: TRIAGE_KEYS })
  // design-language §7: blocked (red-orange octagon, the reason in words) vs needs you (amber ◆).
  const blocked = row.blocking && isBlockedRow(row)
  const needs = row.blocking && !blocked
  const tone: BreatheTone | undefined = blocked ? 'blocked' : needs ? 'wait' : undefined
  useBreathePause(li, !!(tone && r.breathe))
  const glyphTone = row.risky ? 'is-fail' : row.blocking ? 'is-wait' : ''
  const name = `${row.title}. ${blocked ? 'Blocked' : KIND_LABEL[row.kind]}${row.projectName ? `, ${row.projectName}` : ''}${needs ? ', needs you' : ''}`
  return (
    <li
      ref={li}
      className={`tr-row${r.focused ? ' is-focused' : ''}${r.current ? ' is-current' : ''}${r.selected ? ' is-selected' : ''}${blocked ? ' is-blocked' : needs ? ' is-needs' : ''}${r.breathe ? breatheClass(tone, r.breathe) : ''}`}
      data-row-key={row.key}
      data-kind={row.kind}
      {...acts.rowProps}
      onPointerDown={(e) => {
        if (e.pointerType !== 'touch') return
        press.current = setTimeout(() => {
          press.current = undefined
          r.onToggle()
        }, 550)
      }}
      onPointerUp={() => clearTimeout(press.current)}
      onPointerCancel={() => clearTimeout(press.current)}
      onPointerMove={() => clearTimeout(press.current)}
    >
      <span className="tr-row__lead">
        <span className={`tr-glyph vk-icol ${glyphTone}`}>
          {blocked ? (
            <StatusIcon kind="blocked" label="Blocked" />
          ) : needs && row.kind === 'decision' ? (
            <StatusIcon kind="wait" label="Needs you" />
          ) : (
            KIND_ICON[row.kind]
          )}
        </span>
        {r.selectable === false ? null : (
          <input
            type="checkbox"
            checked={r.selected}
            onChange={r.onToggle}
            aria-label={`Select ${row.title}`}
            tabIndex={-1}
          />
        )}
      </span>
      <button
        type="button"
        className="tr-row__main"
        data-row-main
        aria-label={name}
        aria-current={r.current ? 'true' : undefined}
        tabIndex={r.focused || r.current || r.quickTabbable ? 0 : -1}
        onClick={r.onOpen}
      >
        <span className="tr-row__title">
          {row.locked ? 'Encrypted: unlock this browser to read' : rowTitle(row)}
        </span>
        {/* app-clarity: one meta sentence in words, no chips; context and answers live in the
            inspector (and 1–9 from the list). */}
        <span className="tr-row__meta">
          {blocked ? <b className="tr-blocked">Blocked</b> : null}
          {rowMeta(row).map((x, i) => (
            <span key={x}>
              {i || blocked ? <span aria-hidden="true">{' · '}</span> : null}
              {x}
            </span>
          ))}
        </span>
        {row.note ? <span className="tr-row__note">{row.note}</span> : null}
      </button>
      <span className="tr-row__side">
        <RowAge ts={row.createdAt} />
        {row.quick ? (
          <span className="tr-quick" role="group" aria-label={`Quick answers for ${row.title}`}>
            {row.quick.map((q) => (
              <button
                key={q.id}
                type="button"
                tabIndex={r.quickTabbable ? 0 : -1}
                className="vk-opt"
                title={q.label}
                aria-keyshortcuts={String(q.key)}
                onClick={() => r.onQuick(q.id)}
              >
                <OptionKey n={q.key} picked={false} />
                <span>{q.label}</span>
                {q.suggested ? <SuggestedPill /> : null}
              </button>
            ))}
          </span>
        ) : r.onApprove ? (
          <span className="tr-quick" role="group" aria-label={`Approve ${row.title}`}>
            <button
              type="button"
              className="vk-opt"
              tabIndex={-1}
              onClick={() => r.onApprove?.('allow')}
            >
              <span>Allow once</span>
            </button>
            <button
              type="button"
              className="vk-opt"
              tabIndex={-1}
              onClick={() => r.onApprove?.('deny')}
            >
              <span>Deny</span>
            </button>
          </span>
        ) : null}
      </span>
      <RowActionBar api={acts} label={row.title} />
    </li>
  )
}

/** The row's age, compact ("42m"); the full time is the tooltip. */
export function RowAge({ ts }: { ts: number }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])
  return (
    <time dateTime={new Date(ts).toISOString()} title={new Date(ts).toLocaleString()}>
      {shortAgo(ts, now)}
    </time>
  )
}

/** Decisions snoozed through the synced entity ("Later"), with Wake. */
function SnoozedDecisions({ rows }: { rows: readonly InboxRow[] }) {
  const act = useDecisionActions()
  const [open, setOpen] = useState(false)
  const list = rows.filter((r) => r.view?.decision)
  if (!list.length) return null
  return (
    <section className="px-4 pt-4" aria-label="Snoozed decisions">
      <button
        type="button"
        className="vk-btn vk-btn--ghost vk-btn--sm"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <AlarmClock size={14} aria-hidden="true" />
        <span>
          {open ? 'Hide' : 'Show'} {list.length} snoozed{' '}
          {list.length === 1 ? 'decision' : 'decisions'}
        </span>
      </button>
      {open ? (
        <ul className="tr-assumptions mt-2">
          {list.map((r) => (
            <li key={r.key} className="!flex items-center gap-2">
              <span className="min-w-0 flex-1">{r.title}</span>
              <button
                type="button"
                className="vk-btn vk-btn--ghost vk-btn--sm"
                onClick={() => r.view && void act.snooze(r.view, Date.now())}
              >
                Wake
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
